import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OrderService from '../../../services/order.service';
import OrderDetails from './OrderDetails';
import TransactionDetailsModal from './TransactionDetailsModal';

const styles = {
  container: {
    width: '100%',
    backgroundColor: '#f8f9fa',
    p: 3
  },
  paper: {
    p: 3,
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
  },
  uploadSection: {
    my: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  hiddenInput: {
    display: 'none'
  },
  resultSection: {
    mt: 4
  },
  statsCard: {
    bgcolor: '#f5f5f5',
    p: 2,
    borderRadius: 2,
    mb: 2
  },
  transactionList: {
    maxHeight: '400px',
    overflow: 'auto'
  }
};

export default function CheckTransactions() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [provider, setProvider] = useState('fedapay');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    console.log('Fichier sélectionné:', selectedFile);
    
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv') || selectedFile.type === 'text/csv') {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Veuillez sélectionner un fichier CSV valide');
        setFile(null);
      }
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier CSV');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      
      const response = await OrderService.checkTransactions(formData);
      console.log('Réponse du serveur:', response);
      
      if (response.status === 'error') {
        throw new Error(response.message || 'Une erreur est survenue');
      }
      
      setResult(response);
    } catch (err) {
      console.error('Erreur complète:', err);
      setError(err.message || 'Une erreur est survenue lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionDetails = async (transactionId) => {
    try {
      setLoading(true);
      console.log('Début de la vérification pour transactionId:', transactionId);
      
      const response = await OrderService.verifyTransaction({
        transaction_id: transactionId,
        provider: provider
      });
      
      console.log('Réponse complète de la vérification:', response);
      
      if (response) {
        setTransactionDetails(response);
        console.log('TransactionDetails mis à jour avec:', response);
      } else {
        throw new Error('Réponse vide du serveur');
      }
    } catch (err) {
      console.error('Erreur complète:', err);
      setError(err.message || 'Erreur lors de la vérification de la transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        <Typography variant="h5" gutterBottom>
          Vérification des Transactions Fedapay
        </Typography>

        <Box sx={styles.uploadSection}>
          <input
            style={styles.hiddenInput}
            id="csv-file"
            type="file"
            onChange={handleFileChange}
            accept=".csv,text/csv"
          />
          <label htmlFor="csv-file">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
            >
              Sélectionner le fichier CSV
            </Button>
          </label>
          {file && (
            <Typography variant="body2">
              Fichier sélectionné : {file.name}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="provider-select-label">Provider</InputLabel>
            <Select
              labelId="provider-select-label"
              id="provider-select"
              value={provider}
              label="Provider"
              onChange={(e) => setProvider(e.target.value)}
            >
              <MenuItem value="fedapay">Fedapay</MenuItem>
              <MenuItem value="kkiapay">KKiapay</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!file || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Vérifier les transactions'}
          </Button>
        </Box>

        {result && result.status === 'success' && (
          <Box sx={styles.resultSection}>
            <Alert severity="success" sx={{ mb: 3 }}>
              {result.message}
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={styles.statsCard}>
                  <Typography variant="h6" gutterBottom>
                    Résumé des transactions
                  </Typography>
                  <Typography>
                    Total des transactions : {result.total_transactions}
                  </Typography>
                  <Typography>
                    Transactions existantes : {result.total_existing}
                  </Typography>
                  <Typography>
                    Transactions manquantes : {result.total_missing}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={12}>
                <Paper sx={styles.statsCard}>
                  <Typography variant="h6" gutterBottom>
                    Transactions manquantes ({result.total_missing})
                  </Typography>
                  <TableContainer sx={styles.transactionList}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID Transaction</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Montant</TableCell>
                          <TableCell>Nom</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.missing_transactions.map((transaction, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography color="error" variant="body2">
                                {transaction.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {/* {new Date((transaction.created_at - 25569) * 86400 * 1000).toLocaleString('fr-FR')} */}
                                {transaction.created_at}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {Number(transaction.amount).toLocaleString('fr-FR')} FCFA
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {transaction.customer_lastname} {transaction.customer_firstname}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {transaction.customer_email}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleTransactionDetails(transaction.id)}
                                color="primary"
                                sx={{ mr: 1 }}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
{/* 
              <Grid item xs={12}>
                <Paper sx={styles.statsCard}>
                  <Typography variant="h6" gutterBottom>
                    Transactions existantes
                  </Typography>
                  <Box sx={styles.transactionList}>
                    {result.existing_transactions.map((transactionId, index) => (
                      <Chip
                        key={index}
                        label={transactionId}
                        sx={{ m: 0.5 }}
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid> */}
            </Grid>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <OrderDetails 
        order={selectedOrder}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />

      <TransactionDetailsModal
        open={!!transactionDetails}
        onClose={() => setTransactionDetails(null)}
        data={transactionDetails}
        provider={provider}
      />
    </Box>
  );
} 